import React from 'react';
import { Grid, TextField, MenuItem, InputAdornment, Button, Dialog, DialogTitle, DialogContent, DialogActions, Box } from '@mui/material';
import { Search as SearchIcon, DateRange as DateRangeIcon } from '@mui/icons-material';

const textFieldStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1,
    fontSize: { xs: '0.675rem', sm: '0.875rem' },
    '& fieldset': {
      borderColor: '#e2e8f0',
    },
    '&:hover fieldset': {
      borderColor: '#cbd5e1',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1f1f1f',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: {  xs: '0.675rem', sm: '0.875rem' },
    color: '#737373',
    '&.Mui-focused': {
      color: '#1f1f1f',
    },
  },
};

const GlobalFilters = ({
  filters,
  onFilterChange,
  projects,
  authors,
  repositories,
  searchTerm,
  onSearchChange,
  showProjectFilter,
  showDateRangeFilter,
  showCustomRangeFilter,
  showAuthorFilter,
  showRepoFilter,
  showSeverityFilter,
  showStatusFilter,
  showSearchFilter,
}) => {
  const [isCustomOpen, setIsCustomOpen] = React.useState(false);
  const [customLocal, setCustomLocal] = React.useState({ start: '', end: '' });

  const handleOpenCustom = () => {
    setCustomLocal({ start: filters.customStart || '', end: filters.customEnd || '' });
    setIsCustomOpen(true);
  };
  const handleCloseCustom = () => {
    setIsCustomOpen(false);
  };
  const handleApplyCustom = () => {
    onFilterChange('customStart', customLocal.start);
    onFilterChange('customEnd', customLocal.end);
    onFilterChange('dateRange', 'custom');
    setIsCustomOpen(false);
  };

  const handleDateRangeChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      handleOpenCustom();
      return;
    }
    onFilterChange('dateRange', value);
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
      {showProjectFilter && (
        <TextField
          select
          label="Project"
          value={filters.projectKey || ''}
          onChange={(e) => onFilterChange('projectKey', e.target.value)}
          size="small"
          aria-label="Select project to filter dashboard data"
          role="combobox"
          sx={{
            minWidth: 200,
            ...textFieldStyles
          }}
          SelectProps={{
            MenuProps: {
              disableScrollLock: true,
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            },
          }}
        >
          {projects.map((proj) => (
            <MenuItem key={proj.api_key} value={proj.api_key}>
              {proj.name || proj.project_name || 'Unnamed Project'}
            </MenuItem>
          ))}
        </TextField>
      )}
      {showDateRangeFilter && (
        <TextField
          select
          label="Date Range"
          name="dateRange"
          value={filters.dateRange || '30d'}
          onChange={handleDateRangeChange}
          size="small"
          aria-label="Select date range for dashboard data"
          role="combobox"
          sx={{
            minWidth: 200,
            ...textFieldStyles
          }}
          SelectProps={{
            MenuProps: {
              disableScrollLock: true,
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            },
          }}
        >
          <MenuItem value="7d">Last 7 Days</MenuItem>
          <MenuItem value="30d">Last 30 Days</MenuItem>
          <MenuItem value="90d">Last 90 Days</MenuItem>
          <MenuItem value="180d">Last 180 Days</MenuItem>
          <MenuItem value="overall">Overall</MenuItem>
          <MenuItem value="custom">Custom</MenuItem>
        </TextField>
      )}
      {showCustomRangeFilter && (
        <Button
          variant="outlined"
          onClick={handleOpenCustom}
          startIcon={<DateRangeIcon fontSize='6px'/>}
          sx={{
            borderRadius: 1,
            textTransform: 'none',
            color: '#374151',
            borderColor: '#e2e8f0',
            height: '40px',
            minWidth: 200,
            fontSize: { xs: '0.675rem', sm: '0.875rem' },
            fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            '&:hover': {
              borderColor: '#cbd5e1',
              backgroundColor: '#f8fafc',
            },
          }}
        >
          Custom Range
        </Button>
      )}

      <Dialog open={isCustomOpen} onClose={handleCloseCustom} maxWidth="xs" fullWidth>
        <DialogTitle>Select Custom Range</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 5, mt: 1 }}>
            <TextField
              type="date"
              label="From"
              name="customStart"
              value={customLocal.start}
              onChange={(e) => setCustomLocal((prev) => ({ ...prev, start: e.target.value }))}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label="To"
              name="customEnd"
              value={customLocal.end}
              onChange={(e) => setCustomLocal((prev) => ({ ...prev, end: e.target.value }))}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCustom}>Cancel</Button>
          <Button
            onClick={handleApplyCustom}
            variant="contained"
            disabled={!customLocal.start || !customLocal.end || new Date(customLocal.start) > new Date(customLocal.end)}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
      {showAuthorFilter && (
        <TextField
          select
          label="Author"
          name="author"
          value={filters.author || 'all'}
          onChange={(e) => onFilterChange('author', e.target.value)}
          size="small"
          aria-label="Select author to filter dashboard data"
          role="combobox"
          sx={{
            minWidth: 150,
            ...textFieldStyles
          }}
          SelectProps={{
            MenuProps: {
              disableScrollLock: true,
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            },
          }}
        >
          {authors.map((author) => (
            <MenuItem key={author} value={author}>
              {author === 'all' ? 'All Authors' : author}
            </MenuItem>
          ))}
        </TextField>
      )}
      {showRepoFilter && (
        <TextField
          select
          label="Repository"
          name="repository"
          value={filters.repository || 'all'}
          onChange={(e) => onFilterChange('repository', e.target.value)}
          size="small"
          aria-label="Select repository to filter dashboard data"
          role="combobox"
          sx={{
            minWidth: 150,
            ...textFieldStyles
          }}
          SelectProps={{
            MenuProps: {
              disableScrollLock: true,
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            },
          }}
        >
          {repositories.map((repo) => (
            <MenuItem key={repo} value={repo}>
              {repo === 'all' ? 'All Repositories' : repo}
            </MenuItem>
          ))}
        </TextField>
      )}
      {showSeverityFilter && (
        <TextField
          select
          label="Severity"
          name="severity"
          value={filters.severity || 'all'}
          onChange={(e) => onFilterChange('severity', e.target.value)}
          size="small"
          aria-label="Select severity to filter dashboard data"
          role="combobox"
          sx={{
            minWidth: 150,
            ...textFieldStyles
          }}
          SelectProps={{
            MenuProps: {
              disableScrollLock: true,
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            },
          }}
        >
          <MenuItem value="all">All Severities</MenuItem>
          <MenuItem value="critical">Critical</MenuItem>
          <MenuItem value="medium">Medium</MenuItem>
          <MenuItem value="low">Low</MenuItem>
        </TextField>
      )}
      {showStatusFilter && (
        <TextField
          select
          label="Status"
          name="status"
          value={filters.status || 'all'}
          onChange={(e) => onFilterChange('status', e.target.value)}
          size="small"
          aria-label="Select status to filter dashboard data"
          role="combobox"
          sx={{
            minWidth: 150,
            ...textFieldStyles
          }}
          SelectProps={{
            MenuProps: {
              disableScrollLock: true,
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            },
          }}
        >
          <MenuItem value="all">All Statuses</MenuItem>
          <MenuItem value="open">Open</MenuItem>
          <MenuItem value="merged">Merged</MenuItem>
          <MenuItem value="closed">Closed</MenuItem>
        </TextField>
      )}
      {showSearchFilter && (
        <TextField
          label="Search"
          name="search"
          value={searchTerm}
          onChange={onSearchChange}
          size="small"
          sx={{
            minWidth: 200,
            ...textFieldStyles
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      )}
    </Box>
  );
};

export default GlobalFilters;